import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Activity, Bot, User, Cpu } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";

const ACTOR = { ai: { icon: Bot, cls: "bg-teal-100 text-teal-700", label: "AI" }, human: { icon: User, cls: "bg-blue-100 text-blue-700", label: "Human" }, system: { icon: Cpu, cls: "bg-slate-100 text-slate-600", label: "System" } };

export default function ActivityLog() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "executive", "manager", "staff"].includes(user.role)) { navigate("/dashboard"); return; }
    const params = filter ? { actor: filter } : {};
    api.get("/ops/activity", { params }).then((r) => setItems(r.data.activity)).catch(() => {});
  }, [user, loading, navigate, filter]);

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="activity-page">
      <div className="flex items-center gap-2 mb-1"><Activity className="w-5 h-5 text-teal-500" /><p className="text-xs uppercase tracking-[0.16em] text-teal-600 font-semibold">Audit trail</p></div>
      <h1 className="font-display text-3xl lg:text-4xl font-bold text-blue-950">Activity log</h1>
      <div className="mt-4 flex gap-2">
        {["", "ai", "human"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`activity-filter-${f || "all"}`} className={`text-xs rounded-full px-3 py-1.5 font-medium ${filter === f ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-600"}`}>{f ? ACTOR[f].label : "All"}</button>
        ))}
      </div>
      <div className="mt-6 space-y-2">
        {items.length === 0 && <p className="text-slate-400 py-10 text-center">No activity yet.</p>}
        {items.map((a) => {
          const meta = ACTOR[a.actor] || ACTOR.system;
          const Icon = meta.icon;
          return (
            <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-3 flex gap-3 items-start" data-testid="activity-row">
              <span className={`grid place-items-center w-8 h-8 rounded-lg shrink-0 ${meta.cls}`}><Icon className="w-4 h-4" /></span>
              <div className="flex-1">
                <p className="text-sm text-slate-800">{a.action}</p>
                {a.detail && <p className="text-xs text-slate-500">{a.detail}</p>}
                <p className="text-[10px] text-slate-400 mt-0.5">{new Date(a.created_at).toLocaleString()}{a.agent ? ` · ${a.agent} agent` : ""}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
